import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import TruckLogo from './TruckLogo';
import BusinessFooter from './BusinessFooter';

interface PrivacyPolicyProps {
  onClose: () => void;
}

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onClose }) => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-primary text-white shadow-md">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center">
            <button 
              onClick={onClose}
              className="mr-4 p-2 rounded-full hover:bg-primary-dark transition-colors"
              aria-label={t('common.back')}
            >
              <ArrowLeft size={24} />
            </button>
            <div className="flex items-center">
              <TruckLogo size={28} className="text-white mr-2" />
              <h1 className="text-xl font-bold uppercase">COTIZATRIN</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 sm:mb-6">{t('privacy.title')}</h1>
          
          <div className="prose max-w-none text-sm sm:text-base">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mt-5 mb-2">{t('privacy.introduction.title')}</h2>
            <p className="mb-3">
              {t('privacy.introduction.content')}
            </p>

            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mt-5 mb-2">{t('privacy.collectedInfo.title')}</h2>
            <p className="mb-2">{t('privacy.collectedInfo.intro')}:</p>
            <ul className="list-disc pl-5 mb-3">
              <li className="mb-1">{t('privacy.collectedInfo.item1')}</li>
              <li className="mb-1">{t('privacy.collectedInfo.item2')}</li>
              <li className="mb-1">{t('privacy.collectedInfo.item3')}</li>
              <li className="mb-1">{t('privacy.collectedInfo.item4')}</li>
              <li className="mb-1">{t('privacy.collectedInfo.item5')}</li>
              <li className="mb-1">{t('privacy.collectedInfo.item6')}</li>
              <li className="mb-1">{t('privacy.collectedInfo.item7')}</li>
              <li className="mb-1">{t('privacy.collectedInfo.item8')}</li>
            </ul>

            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mt-5 mb-2">{t('privacy.infoUse.title')}</h2>
            <p className="mb-2">{t('privacy.infoUse.intro')}:</p>
            <ul className="list-disc pl-5 mb-3">
              <li className="mb-1">{t('privacy.infoUse.item1')}</li>
              <li className="mb-1">{t('privacy.infoUse.item2')}</li>
              <li className="mb-1">{t('privacy.infoUse.item3')}</li>
              <li className="mb-1">{t('privacy.infoUse.item4')}</li>
              <li className="mb-1">{t('privacy.infoUse.item5')}</li>
              <li className="mb-1">{t('privacy.infoUse.item6')}</li>
              <li className="mb-1">{t('privacy.infoUse.item7')}</li>
              <li className="mb-1">{t('privacy.infoUse.item8')}</li>
            </ul>

            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mt-5 mb-2">{t('privacy.storageSecurity.title')}</h2>
            <p className="mb-3">
              {t('privacy.storageSecurity.content1')}
            </p>
            <p className="mb-3">
              {t('privacy.storageSecurity.content2')}
            </p>

            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mt-5 mb-2">{t('privacy.infoSharing.title')}</h2>
            <p className="mb-3">
              {t('privacy.infoSharing.content')}
            </p>
            <ul className="list-disc pl-5 mb-3">
              <li className="mb-1">{t('privacy.infoSharing.item1')}</li>
              <li className="mb-1">{t('privacy.infoSharing.item2')}</li>
              <li className="mb-1">{t('privacy.infoSharing.item3')}</li>
              <li className="mb-1">{t('privacy.infoSharing.item4')}</li>
            </ul>

            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mt-5 mb-2">{t('privacy.cookies.title')}</h2>
            <p className="mb-3">
              {t('privacy.cookies.content')}
            </p>

            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mt-5 mb-2">{t('privacy.userRights.title')}</h2>
            <p className="mb-2">{t('privacy.userRights.intro')}:</p>
            <ul className="list-disc pl-5 mb-3">
              <li className="mb-1">{t('privacy.userRights.item1')}</li>
              <li className="mb-1">{t('privacy.userRights.item2')}</li>
              <li className="mb-1">{t('privacy.userRights.item3')}</li>
              <li className="mb-1">{t('privacy.userRights.item4')}</li>
              <li className="mb-1">{t('privacy.userRights.item5')}</li>
              <li className="mb-1">{t('privacy.userRights.item6')}</li>
              <li className="mb-1">{t('privacy.userRights.item7')}</li>
            </ul>
            <p className="mb-3">
              {t('privacy.userRights.contact')}
              <a href="mailto:privacidad@cotizatrin.com" className="text-primary hover:underline"> privacidad@cotizatrin.com</a>.
            </p>

            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mt-5 mb-2">{t('privacy.policyChanges.title')}</h2>
            <p className="mb-3">
              {t('privacy.policyChanges.content')}
            </p>

            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mt-5 mb-2">{t('privacy.minors.title')}</h2>
            <p className="mb-3">
              {t('privacy.minors.content')}
            </p>

            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mt-5 mb-2">{t('privacy.contact.title')}</h2>
            <p className="mb-3">
              {t('privacy.contact.content')} <a href="mailto:privacidad@cotizatrin.com" className="text-primary hover:underline">privacidad@cotizatrin.com</a>.
            </p>

            <div className="mt-6 pt-3 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                {t('privacy.lastUpdate')}
              </p>
            </div>
          </div>
        </div>
      </main>

      <BusinessFooter variant="minimal" />
    </div>
  );
};

export default PrivacyPolicy;